/**
 * WeaveIn.ai Platform - Form Library Component (Tailwind CSS)
 * Browse available forms and manage form instances for enrollments
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  X,
  FileText,
  Clock,
  CheckCircle,
  Pencil,
  Trash2,
  Eye,
  Filter,
  List,
  LayoutGrid,
  Play,
  ArrowDown,
  ArrowUp,
  UserPlus,
  Settings,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  ListOrdered,
  AlignLeft,
  Copy,
  ExternalLink,
  Globe,
} from 'lucide-react';

// UI Components
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// API Hooks
import useFormTemplates from '@/hooks/useFormTemplates';
import useFormInstances from '@/hooks/useFormInstances';
import { FormTemplate, FormInstance, FormType } from '@/services/api/forms';
import { FormInstanceEditor } from './FormInstanceEditor';
import { matchesInstanceFilters, isTemplateVisibleForContext } from './formLibraryFilters';
import { getInstanceCategory } from './formInstanceFields';
import { FormRenderer } from './FormRenderer';
import { useResolvedFormTheme } from '@/hooks/useResolvedFormTheme';
import { formPageUrl } from './FormPage';
import PublicFormShareDialog from './PublicFormShareDialog';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useRole } from '@/contexts/RoleContext';
import { formsAPI } from '@/services/api/forms';
import { templateVariablesApi, type RequiredInput } from '@/components/messaging/templateVariables';
import { FormStartInputsDialog } from './FormStartInputsDialog';
import { organizationsAPI } from '@/services/api/organizations';
import {
  FORMS_COUNT_PILL,
  FORMS_GHOST_BTN,
  FORMS_MUTED,
  FORMS_PRIMARY_BTN,
  FORMS_SECONDARY_BTN,
  FORMS_SURFACE,
  FORMS_TABLE_HEAD,
  FORMS_TAB_TRIGGER,
  FORMS_TABS_LIST,
} from './formsUi';

interface FormLibraryProps {
  enrollmentId?: number;
  programId?: number;
  memberId?: number;
  organizationId?: number;
  showTemplates?: boolean;
  showInstances?: boolean;
  onClose?: () => void;
  onNavigate?: (tab: string, memberId?: string | number) => void;
  /** Workflow step ID when navigating from a workflow step */
  workflowStepId?: number;
  /** Workflow instance ID when navigating from a workflow step */
  workflowInstanceId?: number;
  /** Form template ID to auto-open when navigating from a workflow step */
  formTemplateId?: number;
  /** Callback when returning to workflow */
  onReturnToWorkflow?: () => void;
  /** TRUS-762: forms started here are filed under this case. */
  caseEpisodeId?: number | null;
}

export const FormLibraryNew: React.FC<FormLibraryProps> = ({
  enrollmentId,
  programId,
  memberId,
  organizationId,
  showTemplates = true,
  showInstances = true,
  onClose,
  onNavigate,
  workflowStepId,
  workflowInstanceId,
  formTemplateId,
  onReturnToWorkflow,
  caseEpisodeId,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { currentRole } = useRole();
  const canCloneTemplates = ['super_admin', 'org_admin', 'ops_admin'].includes(currentRole || '');
  // With a program in context, list THAT program's templates through
  // GET /by-program/{id} — the form_template_programs association plus forms
  // with no program at all — rather than every template in the org (TRUS-762).
  // Without one (the "Forms & consents" page), the whole org's released list,
  // every page of it: the endpoint stops at 50 rows unless asked for more.
  //
  // Released forms only, for everyone including admins. This library is where a
  // form gets STARTED, and an unreleased draft is precisely what should not be
  // fillable — that is why TRUS-571's v2 templates were seeded inactive. Reviewing
  // a draft happens in the Form builder (FormTemplateBuilder), which passes
  // include_drafts.
  const { templates, loading: templatesLoading, error: templatesError } = useFormTemplates({
    is_active: true,
    program_id: programId,
    programScoped: !!programId,
    fetchAll: true,
    autoFetch: showTemplates,
  });

  const { instances, loading: instancesLoading, error: instancesError, createInstance, deleteInstance, refetch: refetchInstances } = useFormInstances({
    enrollment_id: enrollmentId,
    autoFetch: showInstances && !!enrollmentId,
    fetchAll: true,
  });

  // View state: derived from context, NOT from localStorage
  // - No enrollment → always templates (instances require enrollment)
  // - From workflow step → always instances (user is working on a form)
  // - With enrollment → default to instances (user sees their forms first)
  const [view, setView] = useState<'templates' | 'instances'>(
    !enrollmentId ? 'templates' : workflowStepId ? 'instances' : 'instances'
  );
  const [editingInstance, setEditingInstance] = useState<number | null>(null);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState<number | null>(null);
  // Always start with clean filters — no stale localStorage filters that could hide templates
  const [filterFormType, setFilterFormType] = useState<FormType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  // List vs card layout for My Forms (legacy progress notes are easier to scan as a table)
  const [layout, setLayout] = useState<'list' | 'cards'>(() => {
    try { return (localStorage.getItem('form_library_layout') as any) || 'list'; }
    catch { return 'list'; }
  });
  const [sortKey, setSortKey] = useState<'submitted_at' | 'encounter_date' | 'updated_at' | 'status'>('submitted_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  useEffect(() => {
    try { localStorage.setItem('form_library_layout', layout); } catch {}
  }, [layout]);
  const [previewTemplate, setPreviewTemplate] = useState<FormTemplate | null>(null);
  // Brand theme for whichever template the preview modal is showing.
  const previewTheme = useResolvedFormTheme(previewTemplate?.design_template_id);

  // TRUS-545: open a form as its own page, carrying this library's member
  // context in the URL. Only offered when there IS a member — a standalone form
  // page with no member would have nothing to save against. New tab, so the
  // member record the user came from stays where it was.
  const openAsPage = (args: { instanceId?: number; templateId?: number }) => {
    window.open(
      formPageUrl({ ...args, memberId, programId, enrollmentId }),
      '_blank',
      'noopener'
    );
  };
  const [shareTemplate, setShareTemplate] = useState<FormTemplate | null>(null);

  // Clone modal state
  const [cloneTemplate, setCloneTemplate] = useState<FormTemplate | null>(null);
  const [cloneTargetCboId, setCloneTargetCboId] = useState<string>('');
  const [cloneNewName, setCloneNewName] = useState<string>('');
  const [cloneOrgs, setCloneOrgs] = useState<{ id: number; name: string }[]>([]);
  const [cloneLoading, setCloneLoading] = useState(false);

  // Persist only filter state to localStorage (NOT view — view is context-dependent)
  useEffect(() => {
    const stateToSave = {
      filterFormType,
      filterStatus,
      searchTerm
    };
    localStorage.setItem('formLibraryState', JSON.stringify(stateToSave));
  }, [filterFormType, filterStatus, searchTerm]);

  // Check for pending form template on mount (after user returns from member selection)
  useEffect(() => {
    const pendingTemplateId = localStorage.getItem('pendingFormTemplateId');
    if (pendingTemplateId && templates.length > 0) {
      const templateId = parseInt(pendingTemplateId, 10);
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setPreviewTemplate(template);
        // Ensure we're on templates view
        setView('templates');
      }
      // Clear the pending template once we've handled it
      localStorage.removeItem('pendingFormTemplateId');
    }
  }, [templates]);

  // When navigating from a workflow step, switch to instances view and auto-open form
  useEffect(() => {
    if (!workflowStepId || !enrollmentId) return;

    // Always switch to instances view when coming from a workflow step
    setView('instances');

    // Find and open the existing form instance for this workflow step
    // (form instances are auto-created by the backend when the workflow starts
    //  or when list_form_instances is called for the enrollment)
    const autoOpenWorkflowForm = async () => {
      // Find instance matching this workflow step
      const existingInstance = instances.find(
        (inst) => inst.workflow_step_id === workflowStepId
      ) || (formTemplateId ? instances.find(
        (inst) => inst.form_template_id === formTemplateId
      ) : null);

      if (existingInstance) {
        setEditingInstance(existingInstance.id);
      }
      // If no instance found, the backend will create it on next list_form_instances call
      // triggered by refetch — don't create from the frontend to avoid duplicates
    };

    // Only auto-open form when instances have finished loading (not the initial false before fetch)
    if (formTemplateId && !instancesLoading && instances !== undefined) {
      autoOpenWorkflowForm();
    }
  }, [formTemplateId, enrollmentId, workflowStepId, instancesLoading]);

  const formTypes: { value: FormType | 'all'; label: string }[] = [
    { value: 'all', label: t('formsLibrary.type_all', 'All Types') },
    { value: 'assessment', label: t('formsLibrary.type_assessment', 'Assessment') },
    { value: 'progress_note', label: t('formsLibrary.type_progress_note', 'Progress Note') },
    { value: 'intake', label: t('formsLibrary.type_intake', 'Intake Form') },
    { value: 'consent', label: t('formsLibrary.type_consent', 'Consent Form') },
    { value: 'care_plan', label: t('formsLibrary.type_care_plan', 'Care Plan') },
    { value: 'billing', label: t('formsLibrary.type_billing', 'Billing Form') },
    { value: 'custom', label: t('formsLibrary.type_custom', 'Custom Form') },
    { value: 'event_detail', label: t('formsLibrary.type_event_detail', 'Event Detail (Hub / Training)') },
    { value: 'activity_log', label: t('formsLibrary.type_activity_log', 'Activity Log (Outreach)') },
  ];

  const statusOptions = [
    { value: 'all', label: t('formsLibrary.status_all', 'All Statuses') },
    { value: 'draft', label: t('formsLibrary.status_draft', 'Draft') },
    { value: 'submitted', label: t('formsLibrary.status_submitted', 'Submitted') },
    { value: 'reviewed', label: t('formsLibrary.status_reviewed', 'Reviewed') },
    { value: 'approved', label: t('formsLibrary.status_approved', 'Approved') },
  ];

  const sortOptions: { value: typeof sortKey; label: string }[] = [
    { value: 'submitted_at', label: t('formsLibrary.sort_submitted', 'Submission date') },
    { value: 'encounter_date', label: t('formsLibrary.sort_encounter', 'Encounter date') },
    { value: 'updated_at', label: t('formsLibrary.sort_updated', 'Last updated') },
    { value: 'status', label: t('formsLibrary.sort_status', 'Status') },
  ];

  // A template whose fields prefill from `input` variables asks staff for those
  // values first (BE #1220: GET /api/template-variables/required-inputs).
  const [pendingStart, setPendingStart] = useState<{ templateId: number; inputs: RequiredInput[] } | null>(null);

  const handleCreateInstance = async (templateId: number, variableInputs?: Record<string, string> | null) => {
    const template = templates.find(t => t.id === templateId);
    // activity_log (Outreach) and event_detail (Hub/Training) are anonymous —
    // the backend accepts them without an enrollment_id. Only block for all
    // other types where a member context is required.
    const isActivityType =
      template?.form_type === 'activity_log' || template?.form_type === 'event_detail';
    if (!enrollmentId && !isActivityType) {
      alert(t('formsLibrary.no_enrollment_selected', 'No enrollment selected'));
      return;
    }

    if (variableInputs === undefined) {
      try {
        const { required_inputs } = await templateVariablesApi.requiredInputs({ form_template_id: templateId });
        if (required_inputs && required_inputs.length > 0) {
          setPendingStart({ templateId, inputs: required_inputs });
          return;
        }
      } catch {
        // No registry answer: start the form; unfillable fields are left empty.
      }
    }

    const newInstance = await createInstance({
      form_template_id: templateId,
      enrollment_id: enrollmentId,
      ...(caseEpisodeId ? { case_episode_id: caseEpisodeId } : {}),
      ...(variableInputs ? { variable_inputs: variableInputs } : {}),
    });

    if (newInstance) {
      setEditingInstance(newInstance.id);
      setCreatingFromTemplate(null);
      refetchInstances();
    } else {
      // createInstance returned null → the POST /forms/instances/ call failed and
      // the error was swallowed into hook state. Surface it instead of doing
      // nothing (list layout) or dead-ending at a bare "Form Not Found" (cards
      // layout) so the user knows the create did not succeed (TRUS-277).
      alert(t('formsLibrary.create_failed', 'Could not create this form. Please try again; if the problem persists, contact support.'));
    }
  };

  const handleDeleteInstance = async (instance: FormInstance) => {
    if (window.confirm(t('formsLibrary.confirm_delete', 'Delete "{{name}}"? This action cannot be undone.', { name: instance.form_template_name }))) {
      const success = await deleteInstance(instance.id);
      if (success) {
        alert(t('formsLibrary.deleted', 'Form instance deleted'));
        refetchInstances();
      }
    }
  };

  const handleCloseEditor = () => {
    setEditingInstance(null);
    refetchInstances();
  };

  const handleViewTemplate = (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setPreviewTemplate(template);
    }
  };

  const handleOpenClone = async (template: FormTemplate) => {
    setCloneTemplate(template);
    setCloneNewName(template.name);
    setCloneTargetCboId('');
    try {
      const orgs = await organizationsAPI.listOrganizations();
      setCloneOrgs(orgs.map((o: any) => ({ id: o.id, name: o.name })));
    } catch {
      setCloneOrgs([]);
    }
  };

  const handleCloneTemplate = async () => {
    if (!cloneTemplate || !cloneTargetCboId) return;
    setCloneLoading(true);
    try {
      await formsAPI.cloneFormTemplate(
        cloneTemplate.id,
        parseInt(cloneTargetCboId, 10),
        cloneNewName || undefined
      );
      alert(t('formsLibrary.clone_success', 'Template "{{name}}" cloned successfully.', { name: cloneNewName || cloneTemplate.name }));
      setCloneTemplate(null);
    } catch (err: any) {
      alert(err.response?.data?.detail || t('formsLibrary.clone_failed', 'Failed to clone template'));
    } finally {
      setCloneLoading(false);
    }
  };

  const getStatusVariant = (status: string): 'default' | 'warning' | 'info' | 'success' | 'danger' => {
    switch (status) {
      case 'draft':
        return 'warning';
      case 'submitted':
      case 'reviewed':
        return 'info';
      case 'approved':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    const iconClass = "w-4 h-4";
    switch (status) {
      case 'draft':
        return <Clock className={iconClass} />;
      case 'submitted':
      case 'reviewed':
        return <Eye className={iconClass} />;
      case 'approved':
        return <CheckCircle className={iconClass} />;
      default:
        return <FileText className={iconClass} />;
    }
  };

  const getFieldTypeIcon = (fieldType: string) => {
    const iconClass = "w-3 h-3";
    switch (fieldType?.toLowerCase()) {
      case 'text':
      case 'short_text':
        return <Type className={iconClass} />;
      case 'textarea':
      case 'long_text':
        return <AlignLeft className={iconClass} />;
      case 'number':
        return <Hash className={iconClass} />;
      case 'date':
      case 'datetime':
        return <Calendar className={iconClass} />;
      case 'select':
      case 'dropdown':
        return <ListOrdered className={iconClass} />;
      case 'checkbox':
      case 'boolean':
      case 'toggle':
        return <ToggleLeft className={iconClass} />;
      default:
        return <Type className={iconClass} />;
    }
  };

  // Templates that belong on this surface at all. The Form Library is
  // member-facing ("Forms & consents" under Work, and a member profile's Forms
  // tab), so staff-assigned and activity-event templates are excluded — they
  // live under Activity Tracking.
  //
  // Kept SEPARATE from filteredTemplates below because the tab badge counts
  // this, not the search results: the badge read `templates.length` — the raw,
  // unfiltered list — so Somos saw "Available Templates (4)" above a list
  // showing the single member form. The count and the list must come from the
  // same place or they will drift again.
  const contextVisibleTemplates = templates.filter((template) =>
    isTemplateVisibleForContext(template),
  );

  const filteredTemplates = contextVisibleTemplates.filter((template) => {
    // Case-insensitive form type comparison
    if (filterFormType !== 'all' && template.form_type?.toLowerCase() !== filterFormType.toLowerCase()) {
      return false;
    }
    // Search in name and description
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = template.name?.toLowerCase().includes(searchLower);
      const descMatch = template.description?.toLowerCase().includes(searchLower);
      if (!nameMatch && !descMatch) {
        return false;
      }
    }
    return true;
  });

  const filteredInstances = (Array.isArray(instances) ? instances : []).filter((instance) =>
    matchesInstanceFilters(instance, { filterFormType, filterStatus, searchTerm })
  ).slice().sort((a: any, b: any) => {
    const getVal = (i: any) => {
      switch (sortKey) {
        case 'encounter_date':
          // Look for service-date in field_data (progress note + generic)
          return i.field_data?.pn_service_date || i.field_data?.service_date || i.submitted_at || i.updated_at || '';
        case 'updated_at':
          return i.updated_at || i.last_updated_ts || i.submitted_at || '';
        case 'status':
          return i.status || '';
        case 'submitted_at':
        default:
          return i.submitted_at || i.updated_at || i.last_updated_ts || '';
      }
    };
    const va = String(getVal(a) || '');
    const vb = String(getVal(b) || '');
    const cmp = va.localeCompare(vb);
    return sortDir === 'desc' ? -cmp : cmp;
  });

  // If editing an instance, show the editor
  if (editingInstance) {
    return (
      <FormInstanceEditor
        instanceId={editingInstance}
        memberId={memberId}
        programId={programId}
        organizationId={organizationId}
        onClose={handleCloseEditor}
      />
    );
  }

  // If creating from template, show the editor
  if (creatingFromTemplate) {
    return (
      <FormInstanceEditor
        templateId={creatingFromTemplate}
        enrollmentId={enrollmentId}
        memberId={memberId}
        programId={programId}
        organizationId={organizationId}
        onClose={() => {
          setCreatingFromTemplate(null);
          refetchInstances();
        }}
      />
    );
  }

  // One layout toggle, shared by My Forms and the template list.
  const layoutToggle = (
    <div className="flex items-center gap-2">
      <span className={`text-xs uppercase tracking-wider ${FORMS_MUTED}`}>{t('formsLibrary.layout', 'Layout')}</span>
      <div className="inline-flex gap-1" role="group" aria-label={t('formsLibrary.layout', 'Layout')}>
        <Button
          size="sm"
          variant={layout === 'list' ? 'default' : 'secondary'}
          className={layout === 'list' ? FORMS_PRIMARY_BTN : FORMS_SECONDARY_BTN}
          onClick={() => setLayout('list')}
          aria-pressed={layout === 'list'}
        >
          <List className="w-3.5 h-3.5" />
          {t('formsLibrary.layout_list', 'List')}
        </Button>
        <Button
          size="sm"
          variant={layout === 'cards' ? 'default' : 'secondary'}
          className={layout === 'cards' ? FORMS_PRIMARY_BTN : FORMS_SECONDARY_BTN}
          onClick={() => setLayout('cards')}
          aria-pressed={layout === 'cards'}
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          {t('formsLibrary.layout_cards', 'Cards')}
        </Button>
      </div>
    </div>
  );

  const emptyBlock = (title: string, message: string, action?: React.ReactNode) => (
    <div className="text-center py-16">
      <FileText className="w-12 h-12 text-ink-300 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-ink mb-2">{title}</h3>
      <p className={`text-sm mb-6 ${FORMS_MUTED}`}>{message}</p>
      {action}
    </div>
  );

  const dash = <span className="text-ink-300">—</span>;

  return (
    <div className="p-6 bg-surface text-ink">
      {pendingStart && (
        <FormStartInputsDialog
          inputs={pendingStart.inputs}
          onCancel={() => setPendingStart(null)}
          onConfirm={(values) => {
            const { templateId } = pendingStart;
            setPendingStart(null);
            void handleCreateInstance(templateId, values ?? null);
          }}
        />
      )}
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap justify-between items-start gap-2">
        <p className={`text-[13px] ${FORMS_MUTED}`}>
          {enrollmentId
            ? t('formsLibrary.intro_enrollment', 'Browse form templates and manage form instances for this enrollment')
            : t('formsLibrary.intro', 'Browse available form templates')}
        </p>
        {onClose && (
          <Button
            variant="secondary"
            className={FORMS_SECONDARY_BTN}
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            {t('forms.close', 'Close')}
          </Button>
        )}
      </div>

      {(templatesError || instancesError) && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            {templatesError || instancesError}
          </AlertDescription>
        </Alert>
      )}

      {/* View tabs */}
      <Tabs value={view} onValueChange={(v) => setView(v as 'templates' | 'instances')} className="mb-6">
        <TabsList className={FORMS_TABS_LIST}>
          {enrollmentId && (
            <TabsTrigger value="instances" className={FORMS_TAB_TRIGGER}>
              <FileText className="w-4 h-4" />
              <span>{t('forms.my_forms', 'My Forms')}</span>
              <span className={FORMS_COUNT_PILL}>{instances.length}</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="templates" className={FORMS_TAB_TRIGGER}>
            <Plus className="w-4 h-4" />
            <span>{t('forms.available_templates', 'Available Templates')}</span>
            <span className={FORMS_COUNT_PILL}>{contextVisibleTemplates.length}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="border border-v2border rounded-lg p-4 mb-6 bg-surface">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-1 flex items-center justify-center">
            <Filter className="w-5 h-5 text-ink-500" />
          </div>

          <div className="md:col-span-5">
            <Input
              placeholder={t('forms.search_forms', 'Search forms...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-surface border-v2border"
            />
          </div>

          <div className="md:col-span-3">
            <Select value={filterFormType} onValueChange={(value) => setFilterFormType(value as FormType | 'all')}>
              <SelectTrigger>
                <SelectValue placeholder={t('forms.form_type', 'Form Type')} />
              </SelectTrigger>
              <SelectContent>
                {formTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {view === 'instances' && (
            <div className="md:col-span-2">
              <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('forms.status', 'Status')} />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(searchTerm || filterFormType !== 'all' || filterStatus !== 'all') && (
            <div className="md:col-span-1">
              <Button
                variant="ghost"
                size="sm"
                className={FORMS_GHOST_BTN}
                onClick={() => {
                  setSearchTerm('');
                  setFilterFormType('all');
                  setFilterStatus('all');
                }}
              >
                {t('forms.clear_filters', 'Clear Filters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Form Instances View */}
      {view === 'instances' && enrollmentId && (
        <div>
          {/* Layout + sort controls */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            {layoutToggle}
            <div className="flex items-center gap-2">
              <span className={`text-xs uppercase tracking-wider ${FORMS_MUTED}`}>{t('formsLibrary.sort', 'Sort')}</span>
              <div className="w-44">
                <Select value={sortKey} onValueChange={(v) => setSortKey(v as typeof sortKey)}>
                  <SelectTrigger className="h-8 text-xs" aria-label={t('formsLibrary.sort', 'Sort')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="icon"
                variant="secondary"
                className={`${FORMS_SECONDARY_BTN} h-8 w-8`}
                onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')}
                title={sortDir === 'desc' ? t('formsLibrary.newest_first', 'Newest first') : t('formsLibrary.oldest_first', 'Oldest first')}
                aria-label={sortDir === 'desc' ? t('formsLibrary.newest_first', 'Newest first') : t('formsLibrary.oldest_first', 'Oldest first')}
              >
                {sortDir === 'desc' ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {instancesLoading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredInstances.length === 0 ? (
            emptyBlock(
              t('forms.no_forms_found', 'No Forms Found'),
              searchTerm || filterStatus !== 'all'
                ? t('formsLibrary.no_forms_match', 'No forms match your filters')
                : t('formsLibrary.no_forms_yet', 'No forms have been created for this enrollment yet'),
              !searchTerm && filterStatus === 'all' ? (
                <Button
                  variant="default"
                  className={FORMS_PRIMARY_BTN}
                  onClick={() => setView('templates')}
                >
                  <Plus className="w-4 h-4" />
                  {t('forms.create_new_form', 'Create New Form')}
                </Button>
              ) : undefined,
            )
          ) : layout === 'list' ? (
            <div className="rounded-lg border border-v2border overflow-x-auto bg-surface">
              <Table>
                <TableHeader>
                  <TableRow className="border-v2border">
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_template', 'Template')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_category', 'Category')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_title', 'Title / Note')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_encounter', 'Encounter date')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_status', 'Status')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_submitted', 'Submitted')}</TableHead>
                    <TableHead className={`${FORMS_TABLE_HEAD} text-right`}>{t('formsLibrary.col_actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInstances.map((instance: any) => {
                    const encDate = instance.field_data?.pn_service_date || instance.field_data?.service_date;
                    const noteTitle = instance.field_data?.pn_title || instance.field_data?.title;
                    // Surface the note's own Category (e.g. a Progress Note's pn_category)
                    // as its own column, matching the Title / Note treatment (TRUS-301).
                    const noteCategory = getInstanceCategory(instance);
                    return (
                      <TableRow key={instance.id} className="border-v2border">
                        <TableCell className="font-medium text-ink">{instance.form_template_name}</TableCell>
                        <TableCell className="text-ink whitespace-nowrap" title={noteCategory || ''}>
                          {noteCategory || dash}
                        </TableCell>
                        <TableCell className="text-ink max-w-xs truncate" title={noteTitle || ''}>
                          {noteTitle || dash}
                        </TableCell>
                        <TableCell className="text-ink whitespace-nowrap">
                          {encDate ? fmt.short(encDate) : dash}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusVariant(instance.status)} size="sm" className="capitalize">{instance.status}</Badge>
                        </TableCell>
                        <TableCell className={`whitespace-nowrap ${FORMS_MUTED}`}>
                          {instance.submitted_at ? fmt.short(instance.submitted_at) : dash}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="secondary"
                              className={FORMS_SECONDARY_BTN}
                              onClick={() => setEditingInstance(instance.id)}
                            >
                              {t('formsLibrary.open', 'Open')}
                            </Button>
                            {memberId && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className={FORMS_GHOST_BTN}
                                onClick={() => openAsPage({ instanceId: instance.id })}
                                title={t('forms.openAsPage', 'Open as full page')}
                                aria-label={t('forms.openAsPage', 'Open as full page')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInstances.map((instance) => {
                // Parity with the list layout's "Title / Note" column: surface the
                // note's own title (e.g. a Progress Note's pn_title) so cards are
                // distinguishable — otherwise every card just reads the template
                // name ("Progress Note") and the title is missing (TRUS-300).
                const noteTitle = instance.field_data?.pn_title || instance.field_data?.title;
                return (
                <Card key={instance.id} className={`flex flex-col h-full ${FORMS_SURFACE}`}>
                  <CardContent className="p-4 flex-grow">
                    <div className="flex items-start gap-2 mb-2 text-ink-500">
                      {getStatusIcon(instance.status)}
                      <div className="flex-grow min-w-0">
                        <h3 className="text-base font-semibold text-ink">
                          {instance.form_template_name}
                        </h3>
                        {noteTitle && (
                          <p
                            className={`text-sm truncate ${FORMS_MUTED}`}
                            title={noteTitle}
                          >
                            {noteTitle}
                          </p>
                        )}
                      </div>
                    </div>

                    <Badge
                      variant={getStatusVariant(instance.status)}
                      size="sm"
                      className="capitalize mb-4"
                    >
                      {instance.status}
                    </Badge>

                    {/* Completion Progress */}
                    {instance.completion_percentage !== undefined && instance.status === 'draft' && (
                      <div className="mb-4">
                        <div className="flex justify-between mb-1">
                          <span className={`text-xs ${FORMS_MUTED}`}>
                            {t('forms.progress', 'Progress')}
                          </span>
                          <span className={`text-xs ${FORMS_MUTED}`}>
                            {instance.completion_percentage}%
                          </span>
                        </div>
                        <Progress
                          value={instance.completion_percentage}
                          className="h-1.5 bg-paper-2"
                        />
                      </div>
                    )}

                    {/* Metadata */}
                    <div className={`mb-4 space-y-1 text-xs ${FORMS_MUTED}`}>
                      {instance.assigned_to_name && (
                        <p>{t('formsLibrary.meta_assigned', 'Assigned to: {{name}}', { name: instance.assigned_to_name })}</p>
                      )}
                      {instance.due_date && (
                        <p>{t('formsLibrary.meta_due', 'Due: {{date}}', { date: fmt.short(instance.due_date) })}</p>
                      )}
                      {instance.started_at && (
                        <p>{t('formsLibrary.meta_started', 'Started: {{date}}', { date: fmt.short(instance.started_at) })}</p>
                      )}
                      {instance.submitted_at && (
                        <p>{t('formsLibrary.meta_submitted', 'Submitted: {{date}}', { date: fmt.short(instance.submitted_at) })}</p>
                      )}
                      <p>{t('formsLibrary.meta_updated', 'Last updated: {{date}}', { date: fmt.short(instance.last_updated_ts) })}</p>
                    </div>
                  </CardContent>

                  {/* Actions */}
                  <CardFooter className="p-4 pt-4 flex gap-2 border-t border-v2border">
                    {/* flex-1 min-w-0, not fullWidth: `w-full` next to the delete
                        button pushed the pair past the card edge. */}
                    <Button
                      variant="default"
                      className={`flex-1 min-w-0 ${FORMS_PRIMARY_BTN}`}
                      onClick={() => setEditingInstance(instance.id)}
                    >instance.status === 'draft' ? <Pencil className="w-4 h-4" /> : <Eye className="w-4 h-4 shrink-0" />
                      <span className="truncate">
                        {instance.status === 'draft' ? t('formsLibrary.continue', 'Continue') : t('formsLibrary.view', 'View')}
                      </span>
                    </Button>
                    {instance.status === 'draft' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="!text-danger-ink hover:!bg-danger-soft"
                        title={t('formsLibrary.delete', 'Delete')}
                        aria-label={t('formsLibrary.delete', 'Delete')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteInstance(instance);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </CardFooter>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Form Templates View — always render when view is templates OR no enrollment */}
      {(view === 'templates' || !enrollmentId) && (
        <div>
          {templatesLoading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            emptyBlock(
              t('forms.no_templates_found', 'No Templates Found'),
              searchTerm || filterFormType !== 'all'
                ? t('formsLibrary.no_templates_match', 'No templates match your filters. Try clearing filters.')
                : t('formsLibrary.no_templates', 'No form templates are available'),
            )
          ) : (
            /* Templates — list or cards layout, reuses the same toggle as My Forms */
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className={`text-sm ${FORMS_MUTED}`}>
                  {t('formsLibrary.showing_templates', 'Showing {{count}} template(s)', { count: filteredTemplates.length })}
                </p>
                {layoutToggle}
              </div>
              {layout === 'list' ? (
                <div className="rounded-lg border border-v2border overflow-x-auto bg-surface">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-v2border">
                        <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_name', 'Name')}</TableHead>
                        <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_type', 'Type')}</TableHead>
                        <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_version', 'Version')}</TableHead>
                        <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_sections_fields', 'Sections / Fields')}</TableHead>
                        <TableHead className={FORMS_TABLE_HEAD}>{t('formsLibrary.col_description', 'Description')}</TableHead>
                        <TableHead className={`${FORMS_TABLE_HEAD} text-right`}>{t('formsLibrary.col_actions', 'Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTemplates.map((template: any) => {
                        const sectionCount = Array.isArray(template.sections) ? template.sections.length : 0;
                        const fieldCount = Array.isArray(template.sections)
                          ? template.sections.reduce((acc: number, s: any) => acc + (Array.isArray(s.fields) ? s.fields.length : 0), 0)
                          : 0;
                        return (
                          <TableRow key={template.id} className="border-v2border">
                            <TableCell className="font-medium text-ink">
                              <span className="flex items-center gap-1.5 flex-wrap">
                                {template.name}
                                {template.is_required && (
                                  <Badge variant="warning" size="sm">{t('formsLibrary.required', 'Required')}</Badge>
                                )}
                                {template.settings?.is_sample && (
                                  <Badge variant="secondary" size="sm">{t('forms.sample', 'Sample')}</Badge>
                                )}
                                {!template.is_active && (
                                  <Badge variant="default" size="sm">{t('forms.inactive', 'Inactive')}</Badge>
                                )}
                              </span>
                            </TableCell>
                            <TableCell><Badge variant="default" size="sm" className="capitalize">{template.form_type.replace('_', ' ')}</Badge></TableCell>
                            <TableCell className={FORMS_MUTED}>v{template.version}</TableCell>
                            <TableCell className={FORMS_MUTED}>{sectionCount} / {fieldCount}</TableCell>
                            <TableCell className={`max-w-md truncate ${FORMS_MUTED}`} title={template.description || ''}>{template.description || dash}</TableCell>
                            <TableCell>
                              {/* Same actions as the card layout. This used to be an
                                  either/or — Create when an enrollment was in context,
                                  otherwise View — so on a member profile the list showed
                                  ONLY Create, and View and Clone were reachable just by
                                  switching to Cards. Two layouts of the same list should
                                  not offer different actions.

                                  Create stays enrollment-gated: opened from the nav there
                                  is no enrollment and it hard-failed with "No enrollment
                                  selected" (TRUS-213).

                                  Secondary actions are icon-only here to keep the column
                                  narrow, matching FormTemplateBuilder's list. They carry
                                  aria-label as well as title — a tooltip is not an
                                  accessible name. */}
                              <div className="flex items-center justify-end gap-1">
                                {enrollmentId && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className={FORMS_PRIMARY_BTN}
                                    onClick={() => handleCreateInstance(template.id)}
                                  >
                                    {t('forms.create', 'Create')}
                                  </Button>
                                )}
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className={FORMS_GHOST_BTN}
                                  onClick={() => setPreviewTemplate(template)}
                                  title={t('forms.view', 'View')}
                                  aria-label={t('forms.view', 'View')}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {canCloneTemplates && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className={FORMS_GHOST_BTN}
                                    onClick={() => handleOpenClone(template)}
                                    title={t('formsLibrary.clone_to_org', 'Clone to another organization')}
                                    aria-label={t('formsLibrary.clone_to_org', 'Clone to another organization')}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.map((template) => (
                  <Card key={template.id} className={`flex flex-col h-full ${FORMS_SURFACE}`}>
                    <CardContent className="p-4 flex-grow">
                      <div className="flex items-start gap-2 mb-2">
                        <FileText className="w-5 h-5 text-v2accent flex-shrink-0 mt-0.5" />
                        <h3 className="flex-grow text-base font-semibold text-ink">
                          {template.name}
                        </h3>
                        <Badge variant="default" size="sm">v{template.version}</Badge>
                      </div>

                      <div className="mb-3 flex gap-1 flex-wrap">
                        <Badge variant="default" size="sm" className="capitalize">
                          {template.form_type.replace('_', ' ')}
                        </Badge>
                        {template.is_required && (
                          <Badge variant="warning" size="sm">{t('formsLibrary.required', 'Required')}</Badge>
                        )}
                        {/* A starter the platform seeded, not something this org built.
                            Samples arrive inactive — saying so here stops someone
                            wondering why a listed form cannot be used yet. */}
                        {template.settings?.is_sample && (
                          <Badge variant="secondary" size="sm" title={t('forms.sampleHint', 'A starter template. Open it, change what you need, then mark it Active to start using it.')}>
                            {t('forms.sample', 'Sample')}
                          </Badge>
                        )}
                        {!template.is_active && (
                          <Badge variant="default" size="sm">
                            {t('forms.inactive', 'Inactive')}
                          </Badge>
                        )}
                        {template.program_name && (
                          <Badge variant="default" size="sm">
                            {template.program_name}
                          </Badge>
                        )}
                      </div>

                      {template.description && (
                        <p className={`text-sm mb-3 line-clamp-2 ${FORMS_MUTED}`}>
                          {template.description}
                        </p>
                      )}

                      <div className={`text-xs ${FORMS_MUTED}`}>
                        {t('formsLibrary.sections_fields', '{{sections}} sections · {{fields}} fields', {
                          sections: (template.sections || []).length,
                          fields: (template.sections || []).reduce((total: number, section: any) => total + (section.fields || []).length, 0),
                        })}
                      </div>
                    </CardContent>

                    <CardFooter className="p-4 pt-4 border-t border-v2border">
                      {/* Every action shares the row equally and is allowed to
                          SHRINK. `min-w-0` is the part that matters: a flex item
                          defaults to min-width:auto, i.e. never narrower than its
                          own content, so three natural-width buttons simply
                          overflowed the card in the 3-up grid. With min-w-0 they
                          compress and the label ellipsizes instead. Icons keep
                          their own flex-shrink-0 inside Button, so a button that
                          runs out of room still reads as itself. */}
                      <div className="flex items-center gap-2 w-full">
                        {enrollmentId && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleCreateInstance(template.id)}
                            className={`flex-1 min-w-0 ${FORMS_PRIMARY_BTN}`}
                            title={t('forms.create', 'Create')}
                          >
                            <Plus className="w-4 h-4 shrink-0" />
                            <span className="truncate">{t('forms.create', 'Create')}</span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setPreviewTemplate(template)}
                          className={`flex-1 min-w-0 ${FORMS_SECONDARY_BTN}`}
                          title={t('forms.view', 'View')}
                        >
                          <Eye className="w-4 h-4 shrink-0" />
                          <span className="truncate">{t('forms.view', 'View')}</span>
                        </Button>
                        {memberId && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => openAsPage({ templateId: template.id })}
                            className={`flex-1 min-w-0 ${FORMS_SECONDARY_BTN}`}
                            title={t('forms.openAsPage', 'Open as full page')}
                          >
                            <ExternalLink className="w-4 h-4 shrink-0" />
                            <span className="truncate">{t('forms.openAsPage', 'Open as full page')}</span>
                          </Button>
                        )}
                        {canCloneTemplates && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleOpenClone(template)}
                            className={`flex-1 min-w-0 ${FORMS_SECONDARY_BTN}`}
                            title={t('formsLibrary.clone_to_org', 'Clone to another organization')}
                          >
                            <Copy className="w-4 h-4 shrink-0" />
                            <span className="truncate">{t('forms.clone', 'Clone')}</span>
                          </Button>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Clone Template Modal */}
      {cloneTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-v2border text-ink rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-ink">
                {t('formsLibrary.clone_title', 'Clone Template to Organization')}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                className={FORMS_GHOST_BTN}
                onClick={() => setCloneTemplate(null)}
                aria-label={t('forms.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <p className={`text-sm mb-4 ${FORMS_MUTED}`}>
              {t('formsLibrary.clone_intro', 'Clone "{{name}}" to another organization.', { name: cloneTemplate.name })}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">
                  {t('formsLibrary.clone_name', 'Template Name')}
                </label>
                <Input
                  value={cloneNewName}
                  onChange={(e) => setCloneNewName(e.target.value)}
                  placeholder={cloneTemplate.name}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">
                  {t('formsLibrary.clone_target', 'Target Organization')}
                </label>
                <Select value={cloneTargetCboId} onValueChange={setCloneTargetCboId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('formsLibrary.clone_target_ph', 'Select organization...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {cloneOrgs.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="secondary" className={FORMS_SECONDARY_BTN} onClick={() => setCloneTemplate(null)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="default"
                className={FORMS_PRIMARY_BTN}
                onClick={handleCloneTemplate}
                disabled={!cloneTargetCboId || cloneLoading}
              >
                <Copy className="w-4 h-4" />
                {cloneLoading ? t('formsLibrary.cloning', 'Cloning...') : t('formsLibrary.clone_action', 'Clone Template')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-v2border text-ink rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-v2border flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold text-ink">
                  {previewTemplate.name}
                </h2>
                <div className="flex gap-2 mt-2">
                  <Badge variant="default" size="sm" className="capitalize">
                    {previewTemplate.form_type.replace('_', ' ')}
                  </Badge>
                  <Badge variant="default" size="sm">
                    {t('formsLibrary.version_n', 'Version {{n}}', { n: previewTemplate.version })}
                  </Badge>
                  {previewTemplate.program_name && (
                    <Badge variant="default" size="sm">
                      {previewTemplate.program_name}
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={FORMS_GHOST_BTN}
                onClick={() => setPreviewTemplate(null)}
                aria-label={t('forms.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1">
              {previewTemplate.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-ink mb-1">
                    {t('forms.description', 'Description')}
                  </h3>
                  <p className={FORMS_MUTED}>
                    {previewTemplate.description}
                  </p>
                </div>
              )}

              {/* Live form preview — same renderer as the live editor */}
              <div>
                <h3 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  {t('formsLibrary.preview_heading', 'Form Preview ({{sections}} sections, {{fields}} fields)', {
                    sections: (previewTemplate.sections || []).length,
                    fields: (previewTemplate.sections || []).reduce((total, section) => total + (section.fields || []).length, 0),
                  })}
                </h3>
                <FormRenderer
                  sections={previewTemplate.sections || []}
                  disabled
                  showSubmitterBlock
                  theme={previewTheme}
                />
              </div>

              {/* Template Metadata */}
              <div className="mt-6 pt-4 border-t border-v2border">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={FORMS_MUTED}>{t('forms.created', 'Created')}:</span>
                    <span className="ml-2 text-ink">
                      {fmt.short(previewTemplate.created_ts)}
                    </span>
                  </div>
                  {previewTemplate.created_by_name && (
                    <div>
                      <span className={FORMS_MUTED}>{t('forms.created_by', 'Created by')}:</span>
                      <span className="ml-2 text-ink">
                        {previewTemplate.created_by_name}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className={FORMS_MUTED}>{t('forms.status', 'Status')}:</span>
                    <span className="ml-2">
                      <Badge variant={previewTemplate.is_active ? 'success' : 'default'} size="sm">
                        {previewTemplate.is_active ? t('forms.active', 'Active') : t('forms.inactive', 'Inactive')}
                      </Badge>
                    </span>
                  </div>
                  <div>
                    <span className={FORMS_MUTED}>{t('forms.last_updated', 'Last Updated')}:</span>
                    <span className="ml-2 text-ink">
                      {fmt.short(previewTemplate.last_updated_ts || previewTemplate.created_ts)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer with Enhanced Actions */}
            <div className="p-4 border-t border-v2border">
              <div className="flex flex-col gap-3">
                {/* Status and Instructions */}
                {!enrollmentId && (
                  <div className="bg-attention-soft border border-v2border rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <UserPlus className="w-5 h-5 text-attention-ink flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-attention-ink">
                          {t('forms.start_form_options', 'To start this form, attach it to a member — or share a public link')}
                        </p>
                        <p className="text-xs text-attention-ink mt-1">
                          {t('forms.start_form_options_desc', 'Internal use attaches the form to a member\'s enrollment. Public links can be filled out by anyone (e.g. a not-yet-member contact); submissions become a new contact in your organization.')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Primary Actions */}
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <div className={`text-sm ${FORMS_MUTED}`}>
                    {enrollmentId ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-success" />
                        {t('forms.enrollment_selected', 'Enrollment selected - Ready to create form')}
                      </span>
                    ) : memberId ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-v2accent" />
                        {t('forms.member_selected', 'Member selected - Select an enrollment')}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      className={FORMS_SECONDARY_BTN}
                      onClick={() => window.open(`/forms/preview/${previewTemplate.id}`, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('forms.open_in_new_tab', 'Open in new tab')}
                    </Button>
                    <Button
                      variant="secondary"
                      className={FORMS_SECONDARY_BTN}
                      onClick={() => {
                        setShareTemplate(previewTemplate);
                        setPreviewTemplate(null);
                      }}
                    >
                      <Globe className="w-4 h-4" />
                      {t('forms.share_publicly', 'Share publicly')}
                    </Button>
                    <Button
                      variant="secondary"
                      className={FORMS_SECONDARY_BTN}
                      onClick={() => setPreviewTemplate(null)}
                    >
                      {t('forms.close', 'Close')}
                    </Button>
                    {enrollmentId ? (
                      <Button
                        variant="default"
                        className={FORMS_PRIMARY_BTN}
                        onClick={() => {
                          setCreatingFromTemplate(previewTemplate.id);
                          setPreviewTemplate(null);
                        }}
                      >
                        <Play className="w-4 h-4" />
                        {t('forms.start_form', 'Start Form')}
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        className={FORMS_PRIMARY_BTN}
                        onClick={() => {
                          // Store the template ID to use after member selection
                          localStorage.setItem('pendingFormTemplateId', previewTemplate.id.toString());
                          setPreviewTemplate(null);
                          // Navigate to members page if onNavigate is available
                          if (onNavigate) {
                            onNavigate('members');
                          } else {
                            // Fallback - try to find navigation in window context
                            const event = new CustomEvent('navigateToTab', { detail: { tab: 'members' } });
                            window.dispatchEvent(event);
                          }
                        }}
                      >
                        <UserPlus className="w-4 h-4" />
                        {t('forms.go_to_members', 'Go to Members')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {shareTemplate && (
        <PublicFormShareDialog
          templateId={shareTemplate.id}
          templateName={shareTemplate.name}
          programId={shareTemplate.program_id || undefined}
          onClose={() => setShareTemplate(null)}
        />
      )}

    </div>
  );
};

export default FormLibraryNew;
